/* ClassyTea · ATC drawer v2 — intercepts add-to-cart, re-renders the drawer via the
   Section Rendering API, and toggles classes. All timing is in atc-drawer.css.
   ClassyTea build (2026-09-25) on top of the Claude Design v2 handoff:
   - PDP sticky bar ([data-product-form-submit] calls the theme handler directly, no submit event) is intercepted too.
   - "More like this" uses S's order (same maker + pattern > same item type > same maker > Shopify's picks),
     never delays the drawer by more than 400ms, and never shows a blank tile.
   - Theme's own cart drawer + header bubble are refreshed; collection cards get "ADDED ✓" via cart:update. */
(() => {
  /* S, 2026-09-27: every header cart button (main header + scroll-up header) opens the /cart page, never the theme's old cart drawer. */
  document.addEventListener('click', e => {
    const b = e.target.closest && e.target.closest('.header__cart-button, .reveal-header__cart');
    if (!b) return;
    e.preventDefault(); e.stopImmediatePropagation();
    const r = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    if (e.metaKey || e.ctrlKey || e.shiftKey) { window.open(r + 'cart', '_blank'); return; } /* review 2026-09-27: keep new-tab clicks */
    if (location.pathname !== r + 'cart') location.assign(r + 'cart');
  }, true);
  const root = document.querySelector('[data-atc]');
  if (!root) return;
  const SECTION = 'atc-drawer', CLOSE_MS = 1000, REC_N = 4, RECS_WAIT = 400;
  const D = root.dataset;
  const $ = (s, el = root) => el.querySelector(s);
  const body = $('[data-atc-body]'), drawer = $('.atc__drawer'), side = $('[data-atc-recs-side]');
  let addedKey = null, recsHTML = '', lastFocus = null, closeTimer = null, recsSeq = 0, busy = false;

  /* ── PDP button feedback, Claude Design 11f "Ink sweep" (S, 2026-09-27). Main buy box + sticky bar, phone + desktop.
     0ms ink sweep (request fires at the same moment) · label -> check + "Added" once the store confirms (never before 120ms, S 2026-09-27) · drawer opens no earlier than 350ms
     · button resets when the drawer finishes closing · on error: reset and show the normal inline error. ── */
  const FX_SEL = '.product-form__submit, [data-product-form-submit]';
  const CHECK = '<svg class="ct-fx__check" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M2.5 8.5l3.5 3.5 7.5-8" stroke="currentColor" stroke-width="1.8" stroke-linecap="square"/></svg>';
  let fxBtns = [], fxTimer = null;
  const fxText = b => b.querySelector('.atc-text') || b.querySelector(':scope > span:not(.ct-fx__fill)');
  function fxStart(btn, form) {
    if (!btn || !btn.matches(FX_SEL)) return 0;
    /* Gauntlet 2026-09-27 M-F7: animate only this product's buttons (its form's button + the sticky bar tied to that form). */
    const fid = form && form.id;
    const mates = fid ? [...form.querySelectorAll('.product-form__submit'), ...document.querySelectorAll('detached-product-form[data-product-form-id="' + CSS.escape(fid) + '"] [data-product-form-submit]')] : [];
    const all = [btn].concat(mates.filter(b => b !== btn));
    fxReset();
    fxBtns = all;
    all.forEach(b => {
      b.classList.add('ct-fx');
      if (!b.querySelector(':scope > .ct-fx__fill')) { const f = document.createElement('span'); f.className = 'ct-fx__fill'; f.setAttribute('aria-hidden', 'true'); b.prepend(f); }
      b.classList.add('is-adding');
    });
    return performance.now();
  }
  /* S 2026-09-27 20:48: "ADDED" only once the store has confirmed the add (the sweep still starts at the tap). */
  function fxAdded(t0) {
    const wait = Math.max(0, 120 - (performance.now() - t0));
    return new Promise(r => { fxTimer = setTimeout(() => {
      fxBtns.forEach(b => {
        const t = fxText(b); if (!t) return;
        if (!('ctOrig' in t.dataset)) t.dataset.ctOrig = t.innerHTML;
        t.innerHTML = CHECK + '<span>Added</span>';
        b.classList.add('is-added');
      });
      r();
    }, wait); });
  }
  function fxReset() {
    clearTimeout(fxTimer);
    fxBtns.forEach(b => {
      b.classList.remove('is-adding', 'is-added');
      const t = fxText(b); if (t && 'ctOrig' in t.dataset) { t.innerHTML = t.dataset.ctOrig; delete t.dataset.ctOrig; }
    });
    fxBtns = [];
  }

  /* ── intercept every product form (PDP + quick add). Add data-atc-skip to opt out. ── */
  document.addEventListener('submit', e => {
    const form = e.target.closest && e.target.closest('form[action*="/cart/add"]');
    if (!form || form.closest('[data-atc]') || form.hasAttribute('data-atc-skip')) return;
    e.preventDefault();
    e.stopImmediatePropagation(); // stop the theme's own drawer from also opening
    add(form, e.submitter || form.querySelector('[type="submit"]'));
  }, true);

  /* PDP sticky bar: its button calls the theme's handler directly (no submit event). */
  document.addEventListener('click', e => {
    const b = e.target.closest && e.target.closest('detached-product-form [data-product-form-submit]');
    if (!b || b.getAttribute('aria-disabled') === 'true') return;
    const id = b.closest('detached-product-form').dataset.productFormId;
    const form = id && document.getElementById(id);
    if (!form || form.hasAttribute('data-atc-skip')) return;
    e.preventDefault(); e.stopImmediatePropagation();
    add(form, b);
  }, true);

  async function add(form, btn) {
    if (busy) return; busy = true;
    const fx = !!(btn && btn.matches(FX_SEL));
    const t0 = fx ? fxStart(btn, form) : 0;
    const label = btn && !fx && btn.innerHTML;
    if (btn) { if (!fx) btn.disabled = true; btn.setAttribute('aria-busy', 'true'); }
    let variantId = null, ok = false, inCart = false;
    try {
      const fd = new FormData(form);
      variantId = fd.get('id');
      fd.append('sections', SECTION);
      fd.append('sections_url', location.pathname);
      /* Gauntlet 2026-09-27 M-F2: a dropped connection gets a plain message and a 20s limit, never "Failed to fetch". */
      const ac = new AbortController(); const tmo = setTimeout(() => ac.abort(), 20000);
      const res = await fetch(D.cartAddUrl + '.js', { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: fd, signal: ac.signal })
        .catch(() => { throw new Error('Could not add this piece. Please check your connection and try again.'); })
        .finally(() => clearTimeout(tmo));
      const data = await res.json().catch(() => ({})); /* audit #5: never show a raw parse error */
      if (!res.ok) { const er = new Error(data.description || data.message || 'Could not add this piece.'); er.status = res.status; throw er; }
      const item = data.items ? data.items[0] : data;
      const sec = data.sections && data.sections[SECTION]; /* audit #8: section missing, go to /cart */
      if (!item || !item.key || !sec) { if (fx) fxReset(); location.assign('/cart'); return; }
      addedKey = item.key;
      clearError(form);
      if (fx) await fxAdded(t0);
      const token = ++recsSeq;
      const recsP = fetchRecs(item);
      const early = await Promise.race([recsP, new Promise(r => setTimeout(() => r(null), RECS_WAIT))]);
      setRecs(early ? pick(early, sec) : '');
      render(sec);
      if (fx) { const wait = 350 - (performance.now() - t0); if (wait > 0) await new Promise(r => setTimeout(r, wait)); }
      open();
      if (!early) recsP.then(list => {
        if (token !== recsSeq || root.hidden || root.classList.contains('is-closing')) return;
        setRecs(pick(list, sec));
        const strip = $('[data-atc-recs-strip]');
        if (strip) { $('[data-atc-recs-row]').innerHTML = recsHTML; strip.hidden = !recsHTML; }
      }).catch(() => {});
      ok = true;
    } catch (err) {
      if (fx) fxReset();
      /* S 2026-09-27: a collection card whose piece is already in the cart (store says no more) reads "IN YOUR CART", no message over the photo. */
      if (err.status === 422 && btn && form.closest('.ct-atc')) inCart = true;
      else inlineError(form, err.message);
    } finally {
      if (btn) { btn.removeAttribute('aria-busy'); if (!fx) { btn.disabled = false; btn.innerHTML = label; } }
      if (inCart) { btn.classList.add('is-added'); const l = btn.querySelector('.ct-atc__label'); if (l) l.textContent = 'IN YOUR CART'; }
      busy = false;
    }
    if (ok) {
      refreshTheme();
      if (variantId) document.dispatchEvent(new CustomEvent('cart:update', { detail: { productVariantId: String(variantId), source: 'atc-drawer' } }));
    }
  }

  /* ── "More like this" ── */
  const fmt = D.moneyFormat || '${{amount}}';
  function money(cents) {
    const n = (cents || 0) / 100;
    const sep = (v, d, th, dec) => { const [i, f] = v.toFixed(d).split('.'); return i.replace(/\B(?=(\d{3})+(?!\d))/g, th) + (f ? dec + f : ''); };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => ({
      amount: sep(n, 2, ',', '.'), amount_no_decimals: sep(n, 0, ',', '.'),
      amount_with_comma_separator: sep(n, 2, '.', ','), amount_no_decimals_with_comma_separator: sep(n, 0, '.', ',')
    }[k] ?? sep(n, 2, ',', '.')));
  }
  const esc = s => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const strip = (t, v) => v && t.length > v.length && t.slice(0, v.length + 1).toLowerCase() === (v + ' ').toLowerCase() ? t.slice(v.length).trim() : t; /* M-F14 */
  const sized = (url, w) => url ? url.replace(/(\.[a-z]+)(\?|$)/i, `_${w}x$1$2`) : '';
  const clean = s => String(s || '').trim().toLowerCase();
  const okTile = p => p && p.available && p.url && p.title && p.featured_image && Number(p.price) > 0;

  async function getJSON(url, ms) {
    const ac = new AbortController(); const t = setTimeout(() => ac.abort(), ms);
    try { const r = await fetch(url, { signal: ac.signal, credentials: 'same-origin' }); if (!r.ok) return null; return JSON.parse(await r.text()); }
    catch (e) { return null; } finally { clearTimeout(t); }
  }
  const feed = (kind, q) => q ? getJSON(`/collections/${kind}?q=${encodeURIComponent(q)}&view=atc-json`, 2500) : Promise.resolve(null);
  async function shopifyRecs(id) {
    const j = await getJSON(`${D.recsUrl}.json?product_id=${id}&limit=10&intent=related`, 2500);
    return j && Array.isArray(j.products) ? j.products : [];
  }
  /* Order (S, 2026-09-25): 1 same maker AND same pattern · 2 same item type, any maker · 3 same maker · 4 Shopify's picks.
     In stock only, closest price first inside a tier, never the piece just added. */
  async function fetchRecs(item) {
    try {
      const [byMaker, byType, fallback] = await Promise.all([feed('vendors', item.vendor), feed('types', item.product_type), shopifyRecs(item.product_id)]);
      const mk = Array.isArray(byMaker) ? byMaker : [], ty = Array.isArray(byType) ? byType : [];
      const self = mk.concat(ty).find(p => String(p.id) === String(item.product_id));
      const pattern = clean(self && self.pattern);
      const price = item.final_price || item.price || 0;
      const near = l => l.slice().sort((a, b) => Math.abs(a.price - price) - Math.abs(b.price - price));
      const out = [], seen = new Set([String(item.product_id)]);
      for (const list of [near(pattern ? mk.filter(p => clean(p.pattern) === pattern) : []), near(ty), near(mk), fallback]) {
        for (const p of list) {
          if (out.length >= REC_N * 3) break;
          const id = String(p.id);
          if (seen.has(id) || !okTile(p)) continue;
          seen.add(id); out.push(p);
        }
      }
      return out;
    } catch (e) {
      try { return (await shopifyRecs(item.product_id)).filter(p => okTile(p) && String(p.id) !== String(item.product_id)); } catch (e2) { return []; }
    }
  }
  /* drop anything already in the cart, keep 4, build tiles in the handoff's atc-recs markup */
  function pick(list, sectionHTML) {
    sectionHTML = sectionHTML || '';
    const inCart = new Set();
    (sectionHTML.match(/data-product-id="(\d+)"/g) || []).forEach(m => inCart.add(m.replace(/\D/g, '')));
    return (list || []).filter(p => !inCart.has(String(p.id))).slice(0, REC_N).map(p => {
      const t = strip(p.title, p.vendor);
      return `<a class="atc__rec" href="${esc(p.url)}"><img class="atc__rec-img" src="${esc(sized(p.featured_image, 420))}" alt="${esc(t)}" width="208" height="208" loading="lazy"><span class="atc__rec-row"><span class="atc__rec-maker">${esc(p.vendor)}</span><span class="atc__rec-price">${money(p.price)}</span></span><span class="atc__rec-name">${esc(t)}</span></a>`;
    }).join('');
  }
  function setRecs(html) {
    recsHTML = html || '';
    side.innerHTML = recsHTML;
    root.classList.toggle('no-recs', !recsHTML);
  }

  function render(html) {
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const content = doc.querySelector('[data-atc-content]');
    if (!content) return;
    body.replaceChildren(content);
    const tpl = content.querySelector(`template[data-atc-hero][data-key="${CSS.escape(addedKey)}"]`)
             || content.querySelector('template[data-atc-hero]');
    if (tpl) {
      addedKey = tpl.dataset.key;
      $('[data-atc-hero-slot]').replaceChildren(tpl.content.cloneNode(true));
    }
    let others = 0;
    content.querySelectorAll('[data-atc-cell]').forEach(c => { c.hidden = c.dataset.key === addedKey; if (!c.hidden) others++; });
    const also = $('[data-atc-also]'); if (also) also.hidden = others === 0;
    const n = $('[data-atc-also-count]'); if (n) n.textContent = others;
    const strip = $('[data-atc-recs-strip]');
    if (strip) { $('[data-atc-recs-row]').innerHTML = recsHTML; strip.hidden = !recsHTML; }
    syncHeader(content.dataset.count);
  }

  /* quantity + remove, inside the drawer */
  let qtyBusy = false; /* Gauntlet 2026-09-27 M-F3: one change at a time, so fast + / - taps cannot race */
  root.addEventListener('click', async e => {
    if (e.target.closest('[data-atc-close]')) { close(); return; }
    const q = e.target.closest('[data-atc-qty]'), x = e.target.closest('[data-atc-remove]');
    if (!q && !x) return;
    if (qtyBusy) return;
    const el = q || x, key = el.dataset.key, quantity = q ? Math.max(0, +q.dataset.q) : 0;
    el.disabled = true; qtyBusy = true;
    try {
      const res = await fetch(D.cartChangeUrl + '.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: SECTION, sections_url: location.pathname })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !Array.isArray(data.items)) throw new Error(data.description || 'Could not update.');
      refreshTheme();
      if (!data.items.length) { syncHeader(0); close(); return; }
      if (quantity === 0 && key === addedKey) addedKey = data.items[0].key;
      const sec = data.sections && data.sections[SECTION];
      if (!sec) { location.assign('/cart'); return; }
      render(sec);
    } catch (err) { el.disabled = false; }
    finally { qtyBusy = false; }
  });

  function open() {
    clearTimeout(closeTimer);
    lastFocus = document.activeElement;
    root.classList.remove('is-open', 'is-closing');
    root.hidden = false;
    void root.offsetHeight;                 // mount off-screen, then transition
    document.documentElement.classList.add('atc-lock');
    requestAnimationFrame(() => { root.classList.add('is-open'); drawer.focus({ preventScroll: true }); });
    // no auto-close
  }
  function close() {
    if (!root.classList.contains('is-open')) return;
    root.classList.remove('is-open');
    root.classList.add('is-closing');
    document.documentElement.classList.remove('atc-lock');
    closeTimer = setTimeout(() => {
      root.hidden = true; root.classList.remove('is-closing');
      fxReset();
      lastFocus && lastFocus.focus && lastFocus.focus({ preventScroll: true });
    }, CLOSE_MS);
  }
  document.addEventListener('keydown', e => {
    if (!root.classList.contains('is-open')) return;
    if (e.key === 'Escape') return close();
    if (e.key !== 'Tab') return;
    const f = [...root.querySelectorAll('a[href],button:not([disabled])')].filter(n => n.offsetParent);
    if (!f.length) return;
    if (e.shiftKey && (document.activeElement === f[0] || document.activeElement === drawer)) { e.preventDefault(); f[f.length - 1].focus(); } /* M-F8 */
    else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
  });

  function syncHeader(count) {
    document.querySelectorAll('[data-cart-count], .cart-count-bubble span[aria-hidden], .cart-bubble__text-count')
      .forEach(n => { if (!root.contains(n)) n.textContent = count; });
    document.dispatchEvent(new CustomEvent('atc:updated', { detail: { count: +count } }));
  }
  /* keep the theme's own cart drawer (Primavera <cart-items>) in step, without opening it */
  function refreshTheme() {
    try { const ci = document.querySelector('cart-items'); if (ci && typeof ci.refreshCart === 'function') ci.refreshCart(); } catch (e) {}
  }
  function inlineError(form, msg) {
    let el = form.querySelector('[data-atc-error]');
    if (!el) { el = document.createElement('p'); el.setAttribute('data-atc-error', ''); el.setAttribute('role', 'alert'); el.style.cssText = 'color:#B05E00;font-size:14px;margin:8px 0 0'; form.appendChild(el); }
    el.textContent = msg;
    /* Gauntlet 2026-09-27 M-F1: the sticky bar shows only when this form is off screen, so bring the message into view. */
    const r = el.getBoundingClientRect();
    if (r.bottom < 0 || r.top > innerHeight) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }
  function clearError(form) { const el = form && form.querySelector('[data-atc-error]'); if (el) el.remove(); }
  /* review 2026-09-27: a page restored from the back/forward cache must never keep a stuck "Added" button */
  /* Gauntlet 2026-09-27 M-F9: restored from the back/forward cache: drop the stale drawer (its cart may be out of date). */
  window.addEventListener('pageshow', e => {
    if (!e.persisted) return;
    fxReset();
    if (!root.hidden) { clearTimeout(closeTimer); root.classList.remove('is-open', 'is-closing'); root.hidden = true; document.documentElement.classList.remove('atc-lock'); }
  });
  window.ClassyTeaATC = { open, close };
})();
